import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Award } from "lucide-react";
import { toast } from "sonner";
import SpecialistDashboard from "./SpecialistDashboard";

/**
 * Direct-link route: campersnapshot.com/specialist
 * If the user is already authenticated as a specialist, render the dashboard.
 * Otherwise render an inline password prompt (same backend as the Specialist
 * tab on /login) — no bounce through /login required.
 */
export default function SpecialistRoute() {
  const { user, loginSpecialist } = useAuth();
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (user && user.user_type === "specialist") {
    return <SpecialistDashboard />;
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!password) return;
    setBusy(true);
    try {
      await loginSpecialist(password);
      toast.success("Specialist access granted");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid specialist password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-teal-50 flex items-center justify-center px-4">
      <Card className="w-full max-w-md border-2 border-emerald-200 shadow-xl">
        <CardContent className="p-8 space-y-5">
          <div className="text-center space-y-2">
            <div className="w-14 h-14 rounded-2xl bg-emerald-100 mx-auto flex items-center justify-center">
              <Award className="w-7 h-7 text-emerald-700" />
            </div>
            <h1 className="text-2xl font-bold text-emerald-900">Specialist Portal</h1>
            <p className="text-sm text-emerald-700/70">Enter the shared specialist password</p>
          </div>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label htmlFor="specialist-password-input" className="text-xs font-semibold text-emerald-900">Password</Label>
              <Input
                id="specialist-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                data-testid="specialist-route-password-input"
              />
            </div>
            <Button
              type="submit"
              disabled={busy || !password}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="specialist-route-login-btn"
            >
              {busy ? "Signing in…" : "Enter Specialist Portal"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
